// Раскладка дашборда: чистые функции без React — их проще проверить тестами.
// Элемент раскладки: { id, size, hidden }. Размер — доля ширины строки.

export const SIZES = ['third', 'half', 'two_thirds', 'full'];

export const SIZE_LABELS = {
  third: '⅓',
  half: '½',
  two_thirds: '⅔',
  full: 'Вся ширина',
};

export const SIZE_TITLES = {
  third: 'Треть ширины',
  half: 'Половина ширины',
  two_thirds: 'Две трети ширины',
  full: 'Во всю ширину',
};

export const defaultLayout = (widgets) =>
  widgets.map(w => ({ id: w.id, size: w.defaultSize, hidden: false }));

// Сохранённая раскладка + текущий набор виджетов. Незнакомые id (виджет убрали
// или он недоступен роли) отбрасываются, недопустимый размер заменяется
// стандартным. Новые виджеты, которых не было при сохранении, встают туда же,
// где они стоят в стандартной раскладке, — сразу за своим соседом сверху, а не
// в самый конец, где их никто не заметит.
export const mergeLayout = (saved, widgets) => {
  if (!Array.isArray(saved)) return defaultLayout(widgets);
  const byId = Object.fromEntries(widgets.map(w => [w.id, w]));
  const result = [];
  const seen = new Set();
  saved.forEach(item => {
    const w = item && byId[item.id];
    if (!w || seen.has(w.id)) return;
    seen.add(w.id);
    result.push({
      id: w.id,
      size: w.sizes.includes(item.size) ? item.size : w.defaultSize,
      hidden: Boolean(item.hidden),
    });
  });
  widgets.forEach((w, index) => {
    if (seen.has(w.id)) return;
    let insertAt = 0;
    for (let i = index - 1; i >= 0; i -= 1) {
      const pos = result.findIndex(item => item.id === widgets[i].id);
      if (pos !== -1) { insertAt = pos + 1; break; }
    }
    result.splice(insertAt, 0, { id: w.id, size: w.defaultSize, hidden: false });
    seen.add(w.id);
  });
  return result;
};

export const isDefaultLayout = (layout, widgets) => {
  const def = defaultLayout(widgets);
  return layout.length === def.length && layout.every((item, i) =>
    item.id === def[i].id && item.size === def[i].size && !item.hidden);
};

// Переставляет элемент from на место элемента to. При движении вперёд
// элемент встаёт после цели, назад — перед ней: так перетаскивание на
// соседа всегда меняет их местами.
export const moveItem = (layout, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= layout.length || to >= layout.length) return layout;
  const next = [...layout];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

// Сдвиг на одну позицию среди видимых виджетов — для клавиатуры и кнопок.
export const moveVisible = (layout, id, delta) => {
  const visible = layout.filter(item => !item.hidden);
  const pos = visible.findIndex(item => item.id === id);
  const target = visible[pos + delta];
  if (pos === -1 || !target) return layout;
  return moveItem(layout, layout.findIndex(item => item.id === id),
    layout.findIndex(item => item.id === target.id));
};

export const updateItem = (layout, id, patch) =>
  layout.map(item => (item.id === id ? { ...item, ...patch } : item));

// Показанный заново виджет возвращается на своё место в раскладке, но
// переезжает в конец видимых: пользователь только что нажал «Добавить»
// внизу страницы и ждёт увидеть его там, а не где-то наверху.
export const showItem = (layout, id) => {
  const from = layout.findIndex(item => item.id === id);
  if (from === -1) return layout;
  const shown = updateItem(layout, id, { hidden: false });
  let lastVisible = -1;
  shown.forEach((item, i) => { if (!item.hidden && item.id !== id) lastVisible = i; });
  const to = lastVisible < from ? from : lastVisible;
  return moveItem(shown, from, to);
};
